'use client'

/**
 * The interrupted capture the bar offers to continue or finish (#15666).
 *
 * With no idle bar, a resumable interrupted capture is what brings the bar
 * back ("Continue recording"), so it is read on mount and on every route
 * change while nothing is captured — with the menu read's NARROW scope
 * (`RECORDING_MENU_SCOPES`' `'interrupted'`), which skips the calendar read:
 * only the capture is needed here, and the whole menu is the menu's own read
 * when it opens. A running capture is not interrupted, so the row clears
 * while one runs.
 *
 * **Finish** settles it as it stands, with the duration the server measured
 * on the capture's own timeline, then opens its note as Stop does. A reader
 * that sent no duration is refused rather than settling the row at zero, and
 * a failed round trip says so, as the bar's other handlers do.
 *
 * Its own module because `recording-bar-host.tsx` is near its file-size cap.
 */

import { useCallback, useEffect, useState } from 'react'

import { stopRecordingFromBar } from '@/app/actions/recording-bar'
import type {
  RecordingBarMenuResult,
  RecordingMenuInterrupted,
} from '@/app/actions/recording-bar-menu'
import { RECORDING_MENU_SCOPE_PARAM } from '@/lib/notetaker/recording-menu'
import { SHELL_ENDPOINTS, shellRead } from '@/lib/shell-transport'

/** Said when Finish could not reach the server (the bar's wording). */
export const FINISH_INTERRUPTED_FAILED_MESSAGE =
  'Could not finish the recording. Check your connection.'

/** Said when the read carried no duration to settle with. */
export const FINISH_INTERRUPTED_NO_DURATION_MESSAGE =
  'Could not finish the recording yet. Reload the page and try again.'

/** Said when, by the press, the capture is no longer interrupted. */
export const FINISH_INTERRUPTED_GONE_MESSAGE =
  'This recording is no longer interrupted. It may have been continued elsewhere.'

/**
 * The bar's own reader for the interrupted capture: storage decides, so a
 * capture still uploading from another tab or device is never offered. The
 * room's header chip asks it too (#16939), so the two never disagree.
 */
export function readInterruptedCapture(): Promise<RecordingBarMenuResult | null> {
  return shellRead<RecordingBarMenuResult>(SHELL_ENDPOINTS.recordingMenu, {
    [RECORDING_MENU_SCOPE_PARAM]: 'interrupted',
  })
}

export interface InterruptedCaptureInput {
  /** The bar may offer one: the flag is on and this is not `/auth`. */
  available: boolean
  /** Nothing is being captured. */
  sessionIdle: boolean
  /** Read again on every route change. */
  currentPath: string | null
  setIsBusy: (busy: boolean) => void
  setError: (message: string | null) => void
  /** The post-Stop flow's navigation: open the note. */
  openNoteAfterStop: (target: {
    noteId: string | null
    meetingId: string | null
  }) => unknown
}

export interface InterruptedCapture {
  interrupted: RecordingMenuInterrupted | null
  /** The menu's own (whole) read also answers it. */
  setInterrupted: (capture: RecordingMenuInterrupted | null) => void
  finish: (capture: RecordingMenuInterrupted) => Promise<void>
}

export function useInterruptedCapture(
  input: InterruptedCaptureInput,
): InterruptedCapture {
  const {
    available,
    sessionIdle,
    currentPath,
    setIsBusy,
    setError,
    openNoteAfterStop,
  } = input
  const [interrupted, setInterrupted] =
    useState<RecordingMenuInterrupted | null>(null)

  // `currentPath` is the trigger, not an input: every route change re-reads.
  useEffect(() => {
    if (!sessionIdle) {
      setInterrupted(null)
      return
    }
    if (!available) return
    let current = true
    const read = async () => {
      const result = await readInterruptedCapture()
      // An answer for a path, or a session, that has since moved on is not.
      if (!current) return
      setInterrupted(result?.success ? (result.interrupted ?? null) : null)
    }
    void read()
    return () => {
      current = false
    }
  }, [sessionIdle, available, currentPath])

  const finish = useCallback(
    async (shown: RecordingMenuInterrupted) => {
      setIsBusy(true)
      try {
        // Read again at the press: the row on screen may be a route old, and
        // another tab may have resumed it since — settling that would end a
        // live capture at a stale duration. Only the same capture, still
        // interrupted, is settled, and with the duration measured now.
        const fresh = await readInterruptedCapture()
        const capture = fresh?.success ? fresh.interrupted : null
        if (!capture || capture.recordingId !== shown.recordingId) {
          setInterrupted(capture ?? null)
          setError(FINISH_INTERRUPTED_GONE_MESSAGE)
          return
        }
        // Never settled at zero: the row's duration is what was recorded.
        if (capture.recordedMs === undefined) {
          setError(FINISH_INTERRUPTED_NO_DURATION_MESSAGE)
          return
        }
        const settled = await stopRecordingFromBar(
          capture.recordingId,
          capture.recordedMs,
        )
        if (!settled.success) {
          setError(settled.error)
          return
        }
        setInterrupted(null)
        openNoteAfterStop({ noteId: capture.noteId ?? null, meetingId: null })
      } catch {
        // A rejected settle is the bar's connection error — see sub-issue R carve-out
        setError(FINISH_INTERRUPTED_FAILED_MESSAGE)
      } finally {
        setIsBusy(false)
      }
    },
    [openNoteAfterStop, setError, setIsBusy],
  )

  return { interrupted, setInterrupted, finish }
}
